import React, { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatError, logDev } from "../api";
import ResumeManager from "../components/student/ResumeManager";
import MyDataPanel from "../components/MyDataPanel";
import JobCard from "../components/JobCard";
import { useAuth } from "../context/AuthContext";
import { User, BookOpen, FolderGit2, Briefcase, Search, GraduationCap, ShieldCheck, CalendarClock, Sparkles, BarChart3, Mail, ClipboardList, Image as ImageIcon, Loader2, Bot, MessagesSquare, Flame, Trophy } from "lucide-react";
import StudentLMS from "./StudentLMS";
import LMSLesson from "./LMSLesson";
import LMSAssessment from "./LMSAssessment";
import LMSCertificates from "./LMSCertificates";
import Interviews from "./Interviews";
import usePageTitle from "../hooks/usePageTitle";
import Analytics from "./Analytics";
import StudentOffers from "./StudentOffers";
import StudentOnboarding from "./StudentOnboarding";
import StudentCareer from "./StudentCareer";
import StudentPrep from "./StudentPrep";
import StudentCoach from "./StudentCoach";
import StudentSprint from "./StudentSprint";
import StudentConquest from "./StudentConquest";
import { JourneyStepper } from "../components/student/JourneyStepper";
import {
  KpiTile, SectionCard, DashboardHeader, CompletenessBar,
} from "../components/dashboard/shells";

const buildLinks = (subRole, base) => {
  const isPro = subRole === "professional";
  return [
    { key: "home",     to: `${base}/dashboard`, end: true, label: "Overview",  icon: User },
    { key: "profile",  to: `${base}/profile`,   label: "Profile",      icon: User },
    { key: "career",   to: `${base}/career`,    label: "Career AI",    icon: Sparkles },
    { key: "coach",    to: `${base}/coach`,     label: "AI Coach",     icon: Bot },
    { key: "lms",      to: `${base}/lms`,       label: "Learning",     icon: GraduationCap },
    { key: "sprint",   to: `${base}/sprint`,    label: "Skill Sprint", icon: Flame },
    { key: "conquest", to: `${base}/conquest`,  label: "Code Conquest",icon: Trophy },
    { key: "projects", to: `${base}/projects`,  label: "Projects",     icon: FolderGit2 },
    { key: "opps",     to: `${base}/opportunities`,  label: isPro ? "Lateral Roles & Jobs" : "Jobs & Internships", icon: Briefcase },
    { key: "apps",     to: `${base}/applications`,   label: "My Applications",    icon: BookOpen },
    { key: "offers",   to: `${base}/offers`,    label: "Offers",       icon: Mail },
    { key: "iv",       to: `${base}/interviews`,label: "Interviews",   icon: CalendarClock },
    { key: "prep",     to: `${base}/prep`,      label: "Interview Prep", icon: MessagesSquare },
    { key: "onb",      to: `${base}/onboarding`,label: "Onboarding",   icon: ClipboardList },
    { key: "analytics",to: `${base}/analytics`, label: "Analytics",    icon: BarChart3 },
  ];
};

function Overview() {
  usePageTitle("Your Career Hub");
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [apps, setApps] = useState(null);
  const [enrolls, setEnrolls] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [pr, ar, er] = await Promise.all([
          api.get("/me/profile"),
          api.get("/me/applications"),
          api.get("/me/lms/enrollments"),
        ]);
        if (!alive) return;
        setMe(pr.data); setApps(ar.data); setEnrolls(er.data);
      } catch (err) {
        if (err?.response?.status !== 401) {
          logDev.error("[student-overview] initial load failed:", err);
        }
        // Transient 401 right after login: retry once after 400ms
        setTimeout(async () => {
          try {
            const [pr, ar, er] = await Promise.all([
              api.get("/me/profile"),
              api.get("/me/applications"),
              api.get("/me/lms/enrollments"),
            ]);
            if (!alive) return;
            setMe(pr.data); setApps(ar.data); setEnrolls(er.data);
          } catch (retryErr) {
            logDev.error("[student-overview] retry failed:", retryErr);
          }
        }, 400);
      }
    };
    load();
    return () => { alive = false; };
  }, []);
  const p = me?.profile || {};
  const completeness = [p.name, p.institute_name, p.course, p.skills?.length].filter(Boolean).length * 25;
  const badges = p.coe_badges || [];
  const loading = me === null || apps === null || enrolls === null;
  const firstName = (user?.name || me?.user?.name || "").split(" ")[0] || "there";
  const isPro = user?.role === "working_pro";
  const activeApps = (apps || []).filter(a => !["rejected", "withdrawn", "hired"].includes(a.stage)).length;
  const completedLabs = (enrolls || []).filter(e => e.status === "completed").length;

  return (
    <div className="space-y-4">
      <DashboardHeader
        eyebrow={isPro ? "Working Professional · Welcome" : "Welcome back"}
        title={`Hi, ${firstName}`}
        subtitle={
          isPro
            ? (p.current_company
                ? `${p.current_role || "Professional"} at ${p.current_company}${p.years_of_experience ? ` · ${p.years_of_experience} yrs experience` : ""}`
                : "Complete your profile to be matched with lateral roles.")
            : (p.institute_name || "Complete your profile to get discovered by recruiters.")
        }
        primary={{ to: `/${user?.role}/opportunities`, label: "Find opportunities", testid: "cta-find-opps" }}
        secondary={{ to: `/${user?.role}/profile`, label: "Edit profile", testid: "cta-edit-profile" }}
        meta={<CompletenessBar percent={completeness} />}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          icon={Briefcase}
          label="Applications"
          value={apps?.length ?? "—"}
          sub={activeApps > 0 ? `${activeApps} active` : "Get started with your first apply"}
          accent="#006CFF"
          to={`/${user?.role}/applications`}
          testid="kpi-apps"
          loading={loading}
          cta="View all"
        />
        <KpiTile
          icon={GraduationCap}
          label="Active labs"
          value={enrolls?.length ?? "—"}
          sub={`${completedLabs} completed`}
          accent="#9933FF"
          to={`/${user?.role}/lms`}
          testid="kpi-labs"
          loading={loading}
          cta="Open LMS"
        />
        <KpiTile
          icon={ShieldCheck}
          label="CoE badges"
          value={badges.length}
          sub={badges.length ? "Verified by SkillConnect" : "Finish a lab to earn one"}
          accent="#10B981"
          to={`/${user?.role}/lms/certificates`}
          testid="kpi-badges"
          loading={loading}
          cta="Certificates"
        />
        <KpiTile
          icon={Sparkles}
          label="Career AI"
          value={p.employability_score ? `${p.employability_score}` : "—"}
          sub={p.employability_score ? "Employability score" : "Run your first analysis"}
          accent="#F59E0B"
          to={`/${user?.role}/career`}
          testid="kpi-career"
          loading={loading}
          cta="Analyse"
        />
      </div>

      {/* Two-col: Journey (primary, wider) + CoE badges (secondary). */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
        <SectionCard
          eyebrow="Your journey"
          title="Career readiness"
          testid="journey-section"
          pad={false}
        >
          <div className="px-5 pb-5">
            <JourneyStepper />
          </div>
        </SectionCard>

        <SectionCard
          eyebrow="CoE Credentials"
          title="Your trained-at badges"
          action={<Link to={`/${user?.role}/lms/certificates`} className="text-xs link-underline text-signal font-semibold">View all →</Link>}
          testid="badges-section"
        >
          {loading ? (
            <div className="flex flex-wrap gap-2" data-testid="badges-loading">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-8 w-40 bg-slate-100 rounded-md animate-pulse" />
              ))}
            </div>
          ) : badges.length === 0 ? (
            <div className="text-sm text-slate-600" data-testid="no-badges">
              No CoE badges yet. <Link to={`/${user?.role}/lms`} className="text-signal font-semibold hover:underline">Enrol in a lab</Link> to earn your first &ldquo;Trained at CoE&rdquo; credential.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="my-badges">
              {badges.map(b => (
                <span
                  key={b.lab_id}
                  className="inline-flex items-center gap-1.5 font-mono text-xs px-2.5 py-1.5 rounded"
                  style={{ background: `${b.lab_color}15`, color: b.lab_color, border: `1px solid ${b.lab_color}40` }}
                  data-testid={`my-badge-${b.lab_code}`}
                >
                  <ShieldCheck size={12} /> {b.lab_code}
                </span>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <RecommendedForYou />
    </div>
  );
}

function RecommendedForYou() {
  const [recs, setRecs] = useState(null);
  useEffect(() => {
    api.get("/me/recommendations/postings", { params: { limit: 5 } })
      .then(r => setRecs(r.data)).catch(() => setRecs([]));
  }, []);
  if (recs === null) return null;
  return (
    <div className="card card-pad" data-testid="recs-block">
      <div className="flex items-center justify-between">
        <div>
          <span className="eyebrow inline-flex items-center gap-1"><Sparkles size={12} className="text-signal" /> Recommended for you</span>
          <h3 className="font-display font-bold text-xl mt-1">Hand-picked matches</h3>
        </div>
        <Link to="/student/opportunities" className="text-xs link-underline text-signal font-semibold">Browse all →</Link>
      </div>
      {recs.length === 0 ? (
        <p className="text-muted text-sm mt-4" data-testid="no-recs">No recommendations yet — complete your profile to unlock matches.</p>
      ) : (
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {recs.map(p => (
            <div key={p.id} className="p-4 border border-rule rounded-sm hover:border-signal transition" data-testid={`rec-posting-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{p.title}</div>
                  <div className="text-xs text-muted">{p.company_name} · {p.type}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-signal shrink-0">
                  <Sparkles size={12} /> {p.match_score}%
                </span>
              </div>
              <div className="text-xs text-muted mt-2 line-clamp-1">{(p.match_reasons || []).join(" · ")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfilePhoto({ user, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const photo = user?.photo_url || "";

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { setErr("Photo too large (max 4 MB)."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(r.data.photo_url);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    if (!photo) return;
    if (!window.confirm("Remove profile photo?")) return;
    setBusy(true); setErr("");
    try {
      await api.delete("/me/photo");
      onChange?.(null);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="md:col-span-2 flex items-center gap-5" data-testid="profile-photo-section">
      <div className="h-24 w-24 rounded-full bg-paper border border-rule overflow-hidden flex items-center justify-center">
        {photo
          ? <img src={photo} alt="Profile" className="h-full w-full object-cover" data-testid="profile-photo-preview" />
          : <User size={32} className="text-muted" />}
      </div>
      <div className="space-y-2">
        <span className="eyebrow">Profile photo</span>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
               onChange={(e) => upload(e.target.files?.[0])} data-testid="profile-photo-input" />
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="btn-primary" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="profile-photo-upload-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
            {busy ? "Uploading…" : (photo ? "Replace photo" : "Upload photo")}
          </button>
          {photo && (
            <button type="button" className="btn-ghost text-red-600" onClick={remove} disabled={busy} data-testid="profile-photo-delete-btn">
              Remove
            </button>
          )}
        </div>
        <div className="text-[11px] text-muted">PNG / JPG / WebP · ≤ 4 MB</div>
        {err && <div className="text-[11px] text-red-600" data-testid="profile-photo-err">{err}</div>}
      </div>
    </div>
  );
}

function Profile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/me/profile").then(r => { setMe(r.data); setForm(r.data.profile || {}); }); }, []);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onPhotoChange = (url) => setMe((cur) => ({ ...cur, user: { ...(cur?.user || {}), photo_url: url } }));
  const save = async (e) => {
    e.preventDefault();
    setMsg("");
    const payload = { ...form };
    if (typeof payload.skills === "string") payload.skills = payload.skills.split(",").map(s=>s.trim()).filter(Boolean);
    if (typeof payload.software === "string") payload.software = payload.software.split(",").map(s=>s.trim()).filter(Boolean);
    if (payload.graduation_year) payload.graduation_year = Number(payload.graduation_year);
    if (payload.cgpa) payload.cgpa = Number(payload.cgpa);
    // Required fields (mirror backend) so matching + recruiter filtering work.
    const isStudent = me?.user?.role === "student";
    const skillCount = Array.isArray(payload.skills) ? payload.skills.length : 0;
    if (skillCount < 3) {
      setMsg("Add at least 3 skills (comma-separated) so we can match you to the right roles.");
      return;
    }
    if (isStudent && !String(form.course || "").trim()) {
      setMsg("Course is required to complete your profile.");
      return;
    }
    if (isStudent && !payload.graduation_year) {
      setMsg("Graduation year is required to complete your profile.");
      return;
    }
    try { await api.put("/me/profile", payload); setMsg("Saved."); }
    catch (e2) { setMsg(formatError(e2)); }
  };
  if (!me) return null;
  return (
    <form onSubmit={save} className="card card-pad grid md:grid-cols-2 gap-4" data-testid="profile-form">
      <ProfilePhoto user={me.user} onChange={onPhotoChange} />
      {[["name","Full name"],["phone","Phone"],["city","City"],["state","State"],["institute_name","Institute"],["course","Course *"],["graduation_year","Graduation Year *"],["cgpa","CGPA"],["linkedin","LinkedIn URL"],["github","GitHub URL"]].map(([k,l]) => (
        <div key={k}>
          <label className="eyebrow">{l}</label>
          <input className="input mt-2" value={form[k] ?? ""} onChange={on(k)} data-testid={`profile-${k}`} />
        </div>
      ))}
      <div className="md:col-span-2">
        <label className="eyebrow">About</label>
        <textarea className="input mt-2 min-h-[100px]" value={form.about ?? ""} onChange={on("about")} data-testid="profile-about" />
      </div>
      <div>
        <label className="eyebrow">Skills * (comma separated, min 3)</label>
        <input className="input mt-2" value={Array.isArray(form.skills) ? form.skills.join(", ") : (form.skills ?? "")} onChange={on("skills")} data-testid="profile-skills" />
      </div>
      <div>
        <label className="eyebrow">Software (comma separated)</label>
        <input className="input mt-2" value={Array.isArray(form.software) ? form.software.join(", ") : (form.software ?? "")} onChange={on("software")} data-testid="profile-software" />
      </div>
      <div className="md:col-span-2 flex items-center justify-between">
        <span className="text-sm text-muted" data-testid="profile-msg">{msg}</span>
        <button className="btn-primary" data-testid="profile-save-btn">Save profile</button>
      </div>
      <div className="md:col-span-2 mt-6 pt-6 border-t border-rule">
        <ResumeManager />
      </div>
      <div className="md:col-span-2 mt-6 pt-6 border-t border-rule">
        <MyDataPanel />
      </div>
    </form>
  );
}

function MyProjects() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ title: "", description: "", tech: "", url: "" });
  const load = useCallback(() => api.get("/me/projects").then(r => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    const payload = { ...f, tech: f.tech.split(",").map(s=>s.trim()).filter(Boolean) };
    await api.post("/me/projects", payload);
    setF({ title: "", description: "", tech: "", url: "" }); load();
  };
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form onSubmit={add} className="card card-pad md:col-span-1" data-testid="add-project-form">
        <h3 className="font-display font-bold text-xl">Add project</h3>
        <input className="input mt-3" placeholder="Title" required value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} data-testid="proj-title" />
        <textarea className="input mt-3 min-h-[80px]" placeholder="Description" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} data-testid="proj-desc" />
        <input className="input mt-3" placeholder="Tech (comma)" value={f.tech} onChange={(e)=>setF({...f,tech:e.target.value})} data-testid="proj-tech" />
        <input className="input mt-3" placeholder="URL" value={f.url} onChange={(e)=>setF({...f,url:e.target.value})} data-testid="proj-url" />
        <button className="btn-primary w-full justify-center mt-4" data-testid="proj-submit">Add</button>
      </form>
      <div className="md:col-span-2 flex flex-col gap-3">
        {list.length === 0 && <div className="card card-pad text-muted">No projects yet.</div>}
        {list.map(p => (
          <div key={p.id} className="card card-pad" data-testid={`proj-${p.id}`}>
            <div className="flex items-start justify-between">
              <h4 className="font-display font-bold text-lg">{p.title}</h4>
              <button className="btn-ghost" onClick={async ()=>{await api.delete(`/me/projects/${p.id}`); load();}}>Remove</button>
            </div>
            <p className="text-muted text-sm mt-1">{p.description}</p>
            <div className="mt-2 flex gap-2 flex-wrap">{(p.tech||[]).map(t => <span key={t} className="badge">{t}</span>)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Certifications() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ title: "", issuer: "", year: "" });
  const load = useCallback(() => api.get("/me/certifications").then(r=>setList(r.data)), []);
  useEffect(()=>{ load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/me/certifications", { ...f, year: f.year ? Number(f.year) : undefined });
    setF({ title: "", issuer: "", year: "" }); load();
  };
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form onSubmit={add} className="card card-pad" data-testid="add-cert-form">
        <h3 className="font-display font-bold text-xl">Add certification</h3>
        <input className="input mt-3" placeholder="Title" required value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} data-testid="cert-title" />
        <input className="input mt-3" placeholder="Issuer" value={f.issuer} onChange={(e)=>setF({...f,issuer:e.target.value})} data-testid="cert-issuer" />
        <input className="input mt-3" placeholder="Year" value={f.year} onChange={(e)=>setF({...f,year:e.target.value})} data-testid="cert-year" />
        <button className="btn-primary w-full justify-center mt-4" data-testid="cert-submit">Add</button>
      </form>
      <div className="md:col-span-2 flex flex-col gap-3">
        {list.length === 0 && <div className="card card-pad text-muted">No certifications yet.</div>}
        {list.map(c => (
          <div key={c.id} className="card card-pad flex items-center justify-between">
            <div>
              <div className="font-semibold">{c.title}</div>
              <div className="text-sm text-muted">{c.issuer} · {c.year}</div>
            </div>
            <button className="btn-ghost" onClick={async ()=>{await api.delete(`/me/certifications/${c.id}`); load();}}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Opportunities() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [applied, setApplied] = useState(new Set());
  const load = useCallback(
    () => api.get("/postings", { params: { q: q || undefined, type: type || undefined } })
            .then(r => setList(r.data)),
    [q, type],
  );
  useEffect(() => { load(); }, [load]);

  // Mark already-applied postings so we can show a "Applied" pill instead of
  // a duplicate Apply button — drives a much cleaner UX.
  useEffect(() => {
    api.get("/me/applications").then(r => {
      setApplied(new Set(r.data.map(a => a.posting_id)));
    }).catch(() => {});
  }, []);

  const apply = async (p) => {
    try {
      await api.post(`/postings/${p.id}/apply`);
      setApplied(prev => new Set(prev).add(p.id));
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <>
      <div className="bg-white border border-rule rounded-xl p-3 sm:p-4 flex flex-wrap gap-3 items-end" data-testid="opps-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="eyebrow">Search title, skills or company</label>
          <div className="flex items-center gap-2 input mt-2">
            <Search size={16} />
            <input
              className="flex-1 outline-none bg-transparent"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="e.g. Embedded engineer"
              data-testid="opps-q"
            />
          </div>
        </div>
        <div>
          <label className="eyebrow">Type</label>
          <select className="input mt-2" value={type} onChange={(e) => setType(e.target.value)} data-testid="opps-type">
            <option value="">All</option>
            <option value="permanent">Full-time</option>
            <option value="contract">Contract</option>
            <option value="project">Project</option>
            <option value="internship">Internship</option>
          </select>
        </div>
        <button className="btn-primary" onClick={load} data-testid="opps-search-btn">Search</button>
      </div>

      <div className="mt-3 text-sm text-muted" data-testid="opps-count">
        {list.length} {list.length === 1 ? "job" : "jobs"} found
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        {list.map(p => (
          <JobCard
            key={p.id}
            p={p}
            testid={`opp-${p.id}`}
            ctaLabel={applied.has(p.id) ? "Applied" : "Apply"}
            onApply={applied.has(p.id) ? null : apply}
            ctaTo={`/jobs/${p.id}`}
          />
        ))}
        {list.length === 0 && (
          <div className="lg:col-span-2 bg-white border border-rule rounded-xl p-10 text-muted text-center">
            <Briefcase size={28} className="mx-auto opacity-40" />
            <div className="mt-3 font-semibold">No matching jobs right now</div>
            <div className="text-xs mt-1">Try removing filters, or check back later.</div>
          </div>
        )}
      </div>
    </>
  );
}

function MyApps() {
  const [list, setList] = useState([]);
  useEffect(()=>{ api.get("/me/applications").then(r=>setList(r.data)); }, []);
  return (
    <div className="card overflow-x-auto" data-testid="apps-table-wrap">
      <table className="dense">
        <thead><tr><th>Posting</th><th>Company</th><th>Applied</th><th>Status</th></tr></thead>
        <tbody>
          {list.map(a => (
            <tr key={a.id} data-testid={`app-${a.id}`}><td>{a.posting_title}</td><td>{a.company_name}</td><td>{new Date(a.created_at).toLocaleDateString()}</td><td><span className="badge">{a.status}</span></td></tr>
          ))}
          {list.length===0 && <tr><td colSpan={4} className="text-muted">No applications yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const links = buildLinks(user?.sub_role, user?.role === "working_pro" ? "/working_pro" : "/student");
  const title = user?.role === "working_pro" ? "Your Career Hub · Professional" : "Your Career Hub";
  return (
    <Layout title={title} links={links}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="profile" element={<Profile />} />
        <Route path="career" element={<StudentCareer />} />
        <Route path="coach" element={<StudentCoach />} />
        <Route path="prep" element={<StudentPrep />} />
        <Route path="projects" element={<MyProjects />} />
        <Route path="certifications" element={<Certifications />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="applications" element={<MyApps />} />
        <Route path="offers" element={<StudentOffers />} />
        <Route path="lms" element={<StudentLMS />} />
        <Route path="sprint" element={<StudentSprint />} />
        <Route path="conquest" element={<StudentConquest />} />
        <Route path="lms/certificates" element={<LMSCertificates />} />
        <Route path="lms/lessons/:lessonId" element={<LMSLesson />} />
        <Route path="lms/assessments/:assessmentId" element={<LMSAssessment />} />
        <Route path="interviews" element={<Interviews />} />
        <Route path="onboarding" element={<StudentOnboarding />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="*" element={<Overview />} />
      </Routes>
    </Layout>
  );
}
